import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AdminAuthProvider, useAdminAuth } from "./contexts/AdminAuthContext";
import { Toaster } from "sonner";
import AppLayout from "./components/AppLayout";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import MenuPage from "./pages/MenuPage";
import OrdersPage from "./pages/OrdersPage";
import KitchenPage from "./pages/KitchenPage";
import AIBuilderPage from "./pages/AIBuilderPage";
import BranchesPage from "./pages/BranchesPage";
import ConversationalSitePage from "./pages/ConversationalSitePage";
import CheckoutPage from "./pages/CheckoutPage";
import ConversationalBotPage from "./pages/ConversationalBotPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import ChatTakeoverPage from "./pages/ChatTakeoverPage";
import TablesPage from "./pages/TablesPage";
import AgentControlPage from "./pages/AgentControlPage";
import DeliveryFleetPage from "./pages/DeliveryFleetPage";
import DriverPortalPage from "./pages/DriverPortalPage";
import TrackOrderPage from "./pages/TrackOrderPage";
import POSPage from "./pages/POSPage";
import InvoicesPage from "./pages/InvoicesPage";
import AccountingPage from "./pages/AccountingPage";
import LedgerPage from "./pages/LedgerPage";
import OnboardPage from "./pages/OnboardPage";
import TeamPage from "./pages/TeamPage";
import ActivityLogPage from "./pages/ActivityLogPage";
import InventoryPage from "./pages/InventoryPage";
import SmartSuggestionsPage from "./pages/SmartSuggestionsPage";
import OrderAnalyticsPage from "./pages/OrderAnalyticsPage";
import OffersPage from "./pages/OffersPage";
import LiveTablesDashboard from "./pages/LiveTablesDashboard";
import PreviewPage from "./pages/PreviewPage";
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminTenantsPage from "./pages/admin/AdminTenantsPage";
import AdminErrorsPage from "./pages/admin/AdminErrorsPage";
import AdminPerformancePage from "./pages/admin/AdminPerformancePage";
import AdminAlertsPage from "./pages/admin/AdminAlertsPage";
import AdminPdplPage from "./pages/admin/AdminPdplPage";
import AdminAIModelsPage from "./pages/admin/AdminAIModelsPage";
import MarketingCampaignsPage from "./pages/MarketingCampaignsPage";
import AIUsagePage from "./pages/AIUsagePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import CookieConsentBanner from "./components/CookieConsentBanner";
import MyDataPage from "./pages/MyDataPage";
import MagicMenuPage from "./pages/MagicMenuPage";
import QRMenuPage from "./pages/QRMenuPage";
import CustomerMenuPage from "./pages/CustomerMenuPage";
import ReportsPage from "./pages/ReportsPage";
import ReviewsPage from "./pages/ReviewsPage";
import EmailPrefsPage from "./pages/EmailPrefsPage";
import VoiceAgentDashboard from "./pages/VoiceAgentDashboard";
import FlashReportPage from "./pages/FlashReportPage";
import CashWastePage from "./pages/CashWastePage";
import PlanningPage from "./pages/PlanningPage";
import VendorPage from "./pages/VendorPage";
import AIAdvisorPage from "./pages/AIAdvisorPage";
import RestaurantsPage from "./pages/RestaurantsPage";
import CafesPage from "./pages/CafesPage";
import FoodTrucksPage from "./pages/FoodTrucksPage";

function ProtectedRoute({ children }) {
  const { token, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { token, loading } = useAuth();
  if (loading) return null;
  if (token) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminProtectedRoute({ children }) {
  const { token, loading } = useAdminAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
}

function AdminPublicRoute({ children }) {
  const { token, loading } = useAdminAuth();
  if (loading) return null;
  if (token) return <Navigate to="/admin" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <AdminAuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/restaurants" element={<RestaurantsPage />} />
            <Route path="/cafes" element={<CafesPage />} />
            <Route path="/food-trucks" element={<FoodTrucksPage />} />
            <Route path="/demo" element={<OnboardPage />} />
            <Route path="/onboard" element={<OnboardPage />} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/menu/:branchId" element={<CustomerMenuPage />} />
            <Route path="/terms" element={<TermsPage />} />

            {/* Dashboard with persistent sidebar layout */}
            <Route path="/dashboard" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route index element={<DashboardPage />} />

              {/* Full-screen modules (still inside layout) */}
              <Route path="pos" element={<POSPage />} />
              <Route path="kitchen" element={<KitchenPage />} />

              {/* Operations */}
              <Route path="operations/menu" element={<MenuPage />} />
              <Route path="operations/magic-menu" element={<MagicMenuPage />} />
              <Route path="operations/inventory" element={<InventoryPage />} />
              <Route path="operations/orders" element={<OrdersPage />} />
              <Route path="operations/tables" element={<TablesPage />} />
              <Route path="operations/live-tables" element={<LiveTablesDashboard />} />
              <Route path="operations/fleet" element={<DeliveryFleetPage />} />
              <Route path="operations/offers" element={<OffersPage />} />
              <Route path="operations/qr-menu" element={<QRMenuPage />} />

              {/* Finance */}
              <Route path="finance/flash-report" element={<FlashReportPage />} />
              <Route path="finance/accounting" element={<AccountingPage />} />
              <Route path="finance/ledger" element={<LedgerPage />} />
              <Route path="finance/cash-waste" element={<CashWastePage />} />
              <Route path="finance/planning" element={<PlanningPage />} />
              <Route path="finance/vendors" element={<VendorPage />} />
              <Route path="finance/ai-advisor" element={<AIAdvisorPage />} />
              <Route path="finance/smart-suggestions" element={<SmartSuggestionsPage />} />
              <Route path="finance/order-analytics" element={<OrderAnalyticsPage />} />
              <Route path="finance/invoices" element={<InvoicesPage />} />
              <Route path="finance/reports" element={<ReportsPage />} />

              {/* AI */}
              <Route path="ai/agents" element={<AgentControlPage />} />
              <Route path="ai/conversational" element={<ConversationalBotPage />} />
              <Route path="ai/takeover" element={<ChatTakeoverPage />} />
              <Route path="ai/builder" element={<AIBuilderPage />} />
              <Route path="ai/campaigns" element={<MarketingCampaignsPage />} />
              <Route path="ai/reviews" element={<ReviewsPage />} />
              <Route path="ai/usage" element={<AIUsagePage />} />
              <Route path="ai/voice-agent" element={<VoiceAgentDashboard />} />

              {/* Settings */}
              <Route path="settings/branches" element={<BranchesPage />} />
              <Route path="settings/subscription" element={<SubscriptionPage />} />
              <Route path="settings/team" element={<TeamPage />} />
              <Route path="settings/activity-log" element={<ActivityLogPage />} />
              <Route path="settings/my-data" element={<MyDataPage />} />
              <Route path="settings/email-preferences" element={<EmailPrefsPage />} />

              {/* Module index redirects */}
              <Route path="operations" element={<Navigate to="/dashboard/operations/menu" replace />} />
              <Route path="finance" element={<Navigate to="/dashboard/finance/accounting" replace />} />
              <Route path="ai" element={<Navigate to="/dashboard/ai/conversational" replace />} />
              <Route path="settings" element={<Navigate to="/dashboard/settings/branches" replace />} />

              {/* Legacy redirects */}
              <Route path="menu" element={<Navigate to="/dashboard/operations/menu" replace />} />
              <Route path="orders" element={<Navigate to="/dashboard/operations/orders" replace />} />
              <Route path="tables" element={<Navigate to="/dashboard/operations/tables" replace />} />
              <Route path="fleet" element={<Navigate to="/dashboard/operations/fleet" replace />} />
              <Route path="accounting" element={<Navigate to="/dashboard/finance/accounting" replace />} />
              <Route path="invoices" element={<Navigate to="/dashboard/finance/invoices" replace />} />
              <Route path="agents" element={<Navigate to="/dashboard/ai/agents" replace />} />
              <Route path="conversational" element={<Navigate to="/dashboard/ai/conversational" replace />} />
              <Route path="takeover" element={<Navigate to="/dashboard/ai/takeover" replace />} />
              <Route path="builder" element={<Navigate to="/dashboard/ai/builder" replace />} />
              <Route path="branches" element={<Navigate to="/dashboard/settings/branches" replace />} />
              <Route path="subscription" element={<Navigate to="/dashboard/settings/subscription" replace />} />
            </Route>

            {/* Super Admin routes */}
            <Route path="/admin/login" element={<AdminPublicRoute><AdminLoginPage /></AdminPublicRoute>} />
            <Route path="/admin" element={<AdminProtectedRoute><AdminDashboardPage /></AdminProtectedRoute>} />
            <Route path="/admin/tenants" element={<AdminProtectedRoute><AdminTenantsPage /></AdminProtectedRoute>} />
            <Route path="/admin/errors" element={<AdminProtectedRoute><AdminErrorsPage /></AdminProtectedRoute>} />
            <Route path="/admin/performance" element={<AdminProtectedRoute><AdminPerformancePage /></AdminProtectedRoute>} />
            <Route path="/admin/alerts" element={<AdminProtectedRoute><AdminAlertsPage /></AdminProtectedRoute>} />
            <Route path="/admin/pdpl" element={<AdminProtectedRoute><AdminPdplPage /></AdminProtectedRoute>} />
            <Route path="/admin/ai-models" element={<AdminProtectedRoute><AdminAIModelsPage /></AdminProtectedRoute>} />

            {/* External routes */}
            <Route path="/driver" element={<DriverPortalPage />} />
            <Route path="/track/:orderId" element={<TrackOrderPage />} />
            <Route path="/site/:siteId" element={<ConversationalSitePage />} />
            <Route path="/checkout/:checkoutId" element={<CheckoutPage />} />
            <Route path="/preview/:token" element={<PreviewPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <CookieConsentBanner />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AdminAuthProvider>
    </AuthProvider>
  );
}

export default App;
